import React, { useState, useEffect } from "react";
import ClientBackground from "./ClientBackground";
import { ChevronLeft, Bell } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import LoadingSpinner from "./LoadingSpinner";

export default function AlertReminders() {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [timelineReminders, setTimelineReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checklistAlerts, setChecklistAlerts] = useState([]);
  const [hoveredReminder, setHoveredReminder] = useState(null);

  useEffect(() => {
    const colorPalette = ['red', 'orange', 'yellow', 'cyan', 'purple'];
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const upcomingResponse = await api.get(`/children/reminders/upcoming/?child_id=${childId}`);
        const upcomingReminders = upcomingResponse.data || [];
        
        const remindersWithColors = upcomingReminders.slice(0, 5).map((reminder, index) => ({
          ...reminder,
          displayColor: colorPalette[index % colorPalette.length],
          label: formatReminderLabel(reminder.reminder_date)
        }));
        
        setTimelineReminders(remindersWithColors);

        const questionsResponse = await api.get(`/children/weekly-checklist-questions/by-child/?child_id=${childId}`);
        const responsesResponse = await api.get(`/children/weekly-checklist-responses/current-week/?child_id=${childId}`);

        const allQuestions = questionsResponse.data.question_sets?.flatMap((qs) => qs.questions) || [];
        const answeredQuestionIds = new Set(responsesResponse.data?.map((resp) => resp.question) || []);

        const processedAlerts = allQuestions
          .map((question) => ({
            id: question.id,
            title: question.text,
            description: "A friendly reminder to complete this week's checklist item.",
            status: answeredQuestionIds.has(question.id) ? "Completed" : "Pending",
            type: answeredQuestionIds.has(question.id) ? "success" : "pending",
            color: answeredQuestionIds.has(question.id) ? "green" : "yellow",
          }))
          .filter((alert) => alert.status === "Pending");

        setChecklistAlerts(processedAlerts);
      } catch (error) {
        console.error("Error fetching data:", error);
        setTimelineReminders([]);
        setChecklistAlerts([]);
      } finally {
        setLoading(false);
      }
    };

    if (childId) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [childId]);

  const formatReminderLabel = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    
    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return 'th';
      switch (n % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };
    
    return `${day}${getOrdinal(day)}\n${month},\n${year}`;
  };

  const getAlertIcon = (type) => {
    const baseClasses = "w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center text-white font-bold text-sm";
    if (type === "success") {
      return <div className={`${baseClasses} bg-green-500`}>✓</div>;
    }
    return <div className={`${baseClasses} bg-yellow-500`}>!</div>;
  };

  const getAlertCardColor = (color) => {
    if (color === "green") return "bg-green-50 border-green-200";
    if (color === "yellow") return "bg-yellow-50 border-yellow-200";
    return "bg-blue-50 border-blue-200";
  };

  const getTimelineColor = (color) => {
    const colors = {
      red: "bg-red-500",
      orange: "bg-orange-500",
      yellow: "bg-yellow-500",
      cyan: "bg-cyan-500",
      purple: "bg-purple-500",
      teal: "bg-teal-500",
      pink: "bg-pink-500",
      blue: "bg-blue-500",
      green: "bg-green-500"
    };
    return colors[color] || "bg-gray-400";
  };

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ClientBackground />
      <div className="relative z-10 min-h-screen pb-8">
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg md:text-xl font-semibold text-[#1e3a5f]">Alert & Reminders</h1>
          <button className="p-2 relative">
            <Bell className="w-6 h-6 text-[#1e3a5f]" />
            {checklistAlerts.some(a => a.status === "Pending") && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </button>
        </div>

        <div className="px-5 md:px-8 lg:px-12 pt-4">
          <p className="text-sm md:text-base text-gray-600">
            Stay on top of your child's milestones and schedules!
          </p>
        </div>

        <div className="px-5 md:px-8 lg:px-12 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base md:text-lg font-semibold text-[#1e3a5f]">Alerts:</h2>
            <button onClick={() => navigate(`/alert-details/${childId}`)} className="text-sm text-[#1e3a5f] hover:underline">See All</button>
          </div>

          <div className="overflow-x-auto no-scrollbar pb-2 -mx-5 px-5 md:mx-0 md:px-0">
            <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {checklistAlerts.slice(0, 3).map((alert) => (
                <div
                  key={alert.id}
                  className={`min-w-[280px] md:min-w-0 ${getAlertCardColor(alert.color)} rounded-2xl p-4 border-2 flex flex-col`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    {getAlertIcon(alert.type)}
                    <h3 className="text-sm md:text-base font-semibold text-[#1e3a5f] leading-tight flex-1">
                      {alert.title}
                    </h3>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 mb-3 leading-relaxed flex-grow">
                    {alert.description}
                  </p>
                  <div className="flex justify-between items-center mt-auto">
                    <button 
                      onClick={() => navigate(`/weekly-checklist/${childId}`)}
                      className="text-xs md:text-sm text-[#1e3a5f] font-medium hover:underline"
                    >
                      View Details
                    </button>
                    {alert.status === "Completed" ? (
                      <span className="text-xs font-semibold bg-green-500 text-white px-2.5 py-1 rounded-full">
                        Completed
                      </span>
                    ) : (
                      <span className="text-xs font-semibold bg-yellow-500 text-white px-2.5 py-1 rounded-full">
                        Pending
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-5 md:px-8 lg:px-12 mt-10 pt-6 pb-6">
          <div className="flex items-center justify-between mb-6 ">
            <h2 className="text-base md:text-lg font-semibold text-[#1e3a5f]">Reminders:</h2>
            <button onClick={() => navigate(`/reminder-details/${childId}`)} className="text-sm text-[#1e3a5f] hover:underline">See All</button>
          </div>

          {timelineReminders.length > 0 ? (
            <div className="overflow-x-auto no-scrollbar -mx-5 px-5 md:mx-0 md:px-0">
              <div className="min-w-[600px] md:min-w-full py-8">
                <div className="relative">
                  {/* Top Row Labels */}
                  <div className="flex justify-between items-end mb-3">
                    {timelineReminders.map((reminder, index) => (
                      <div 
                        key={`top-${reminder.id}`} 
                        className="flex flex-col items-center justify-end relative group cursor-pointer" 
                        style={{ width: `${100 / timelineReminders.length}%`, visibility: index % 2 === 0 ? 'visible' : 'hidden' }}
                        onMouseEnter={() => setHoveredReminder(reminder.id)}
                        onMouseLeave={() => setHoveredReminder(null)}
                      >

                        
                        {/* Date Label */}
                        <p className={`text-xs md:text-sm font-semibold text-center mb-2 leading-tight ${getTimelineColor(reminder.displayColor).replace('bg-', 'text-')} transition-all ${hoveredReminder === reminder.id ? 'scale-100' : ''}`}>
                          {reminder.label.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              {i < reminder.label.split('\n').length - 1 && <br />}
                            </React.Fragment>
                          ))}
                        </p>
                        
                        {/* Spike */}
                        <div
                          className={`w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[18px] ${getTimelineColor(reminder.displayColor).replace('bg-', 'border-b-')} transition-all ${hoveredReminder === reminder.id ? 'scale-100' : ''}`}
                        ></div>
                      </div>
                    ))}
                  </div>

                  {/* Timeline Bar */}
                  <div className="relative h-3 rounded-full overflow-hidden shadow-md">
                    <div className="absolute inset-0 flex">
                      {timelineReminders.map((reminder) => (
                        <div 
                          key={reminder.id} 
                          className={`flex-1 ${getTimelineColor(reminder.displayColor)} transition-all cursor-pointer ${hoveredReminder === reminder.id ? 'brightness-110' : ''}`}
                          onMouseEnter={() => setHoveredReminder(reminder.id)}
                          onMouseLeave={() => setHoveredReminder(null)}
                        ></div>
                      ))}
                    </div>
                  </div>

                  {/* Bottom Row Labels */}
                  <div className="flex justify-between items-start mt-3">
                    {timelineReminders.map((reminder, index) => (
                      <div 
                        key={`bottom-${reminder.id}`} 
                        className="flex flex-col items-center relative group cursor-pointer" 
                        style={{ width: `${100 / timelineReminders.length}%`, visibility: index % 2 === 1 ? 'visible' : 'hidden' }}
                        onMouseEnter={() => setHoveredReminder(reminder.id)}
                        onMouseLeave={() => setHoveredReminder(null)}
                      >
                        {/* Spike */}
                        <div
                          className={`w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-t-[18px] ${getTimelineColor(reminder.displayColor).replace('bg-', 'border-t-')} mb-2 transition-all ${hoveredReminder === reminder.id ? 'scale-110' : ''}`}
                        ></div>
                        
                        {/* Date Label */}
                        <p className={`text-xs md:text-sm font-semibold text-center leading-tight ${getTimelineColor(reminder.displayColor).replace('bg-', 'text-')} transition-all ${hoveredReminder === reminder.id ? 'scale-100' : ''}`}>
                          {reminder.label.split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              {i < reminder.label.split('\n').length - 1 && <br />}
                            </React.Fragment>
                          ))}
                        </p>


                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No reminders set yet</p>
            </div>
          )}

          <div className="flex justify-center mt-8">
            <button
              onClick={() => navigate(`/add-reminder/${childId}`)}
              className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white px-6 py-3 rounded-full flex items-center gap-2 shadow-lg transition-colors"
            >
              <span className="font-semibold text-sm md:text-base">Add Reminder</span>
              <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                <span className="text-[#1e3a5f] font-bold text-lg">+</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
